import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { LocalWard, CampusWard, ChurchUnitsDataService, CampusWardsDataService } from '../../shared';
import { Title } from '@angular/platform-browser';
import { forkJoin, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  templateUrl: './local-ward-assignments-lookup.component.html',
  styleUrls: ['./local-ward-assignments-lookup.component.scss']
})
export class LocalWardAssignmentsLookupComponent implements OnInit, OnDestroy {

  loading = false;
  loadingError = false;

  localWards: LocalWard[];
  _selectedLocalWard: LocalWard;

  /**
   * Getter for our local ward.
   */
  get selectedLocalWard(): LocalWard {
    return this._selectedLocalWard;
  }

  /**
   * Setter for our local ward; when we set it to a local ward, clear out any selected singles ward.
   */
  set selectedLocalWard(value: LocalWard) {
    this._selectedLocalWard = value;
    if (value) {
      this._selectedCampusWard = null;
    }
  }

  campusWards: CampusWard[];
  _selectedCampusWard: CampusWard;

  /**
   * Getter for our selected campus ward.
   */
  get selectedCampusWard(): CampusWard {
    return this._selectedCampusWard;
  }

  /**
   * Setter for our selected campus ward; when we set the ward to a real ward, clear out any selected local ward.
   */
  set selectedCampusWard(value: CampusWard) {
    this._selectedCampusWard = value;
    if (value) {
      this._selectedLocalWard = null;
    }
  }

  private parameterSubscription: Subscription;

  constructor(private churchService: ChurchUnitsDataService, private wardService: CampusWardsDataService, private router: Router, private route: ActivatedRoute, private titleService: Title) { }

  ngOnInit() {
    this.loading = true;
    forkJoin(
      this.wardService.getCampusWards(),
      this.churchService.getLocalWards()
    )
    .pipe(
      finalize(() => this.loading = false)
    )
    .subscribe(
      ([campusWards, localWards]) => {
        this.campusWards = campusWards.filter(ward => !ward.isForMarriedStudents);
        this.localWards = localWards;

        // pull the values from our URL if they're there, and select
        if (this.route.firstChild) {
          this.parameterSubscription = this.route.firstChild.queryParams.subscribe(params => {
            const campusHomeWardId = +params['campusHomeWardId'];
            const campusWardId = +params['campusWardId'];
            if (campusHomeWardId) {
              this.selectedLocalWard = this.localWards.find(function (ward) {return ward.campusHomeWardId === campusHomeWardId; });
            } else if (campusWardId) {
              this.selectedCampusWard = this.campusWards.find(function (ward) {return ward.campusWardId === campusWardId; });
            }

            this.setTitleBasedOnSelection();
          });
        }
      },
      (error) => {
        this.loadingError = true
      });
  }

  ngOnDestroy() {
    if (this.parameterSubscription) {
      this.parameterSubscription.unsubscribe();
    }
  }

   /**
    * Navigates to the child component to allow assigning local ward(s).
    */
    navigateToLoad(): void {
      if (this.selectedCampusWard || this.selectedLocalWard) {
        const campusWardId = this.selectedCampusWard ? this.selectedCampusWard.campusWardId : undefined;
        const campusHomeWardId = this.selectedLocalWard ? this.selectedLocalWard.campusHomeWardId : undefined;

        this.setTitleBasedOnSelection();

        this.router.navigate(['./'], {
          relativeTo: this.route,
          queryParams: {
            campusWardId: campusWardId,
            campusHomeWardId: campusHomeWardId
          }
        });
      }
    }

    /**
     * Sets our page's title based on if we have a ward or local ward selected.
     */
    private setTitleBasedOnSelection() {
      setTimeout(() => {
        if (this.selectedCampusWard) {
          this.titleService.setTitle(`Manage ${this.selectedCampusWard.name} local ward assignments`);
        } else if (this.selectedLocalWard) {
          this.titleService.setTitle(`Manage ${this.selectedLocalWard.localUnit.name} campus ward assignment`);
        }
      });
    }

}
